/**
 * protocol-version.service.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Injectable } from "@angular/core";
import { Observable } from "rxjs/Rx";
import { ConfigurationService } from "../configuration/configuration.service";
import { SendSignature } from "./message-sender";
import { Message, VersionRequest, VersionResponse }
  from "./messages/general/";

declare const window : Window & {protocolVersion : number};

/**
 * The {@link ProtocolVersionService} handles negotiation with the server about
 * the preferred protocol, and after a successful negotiation,
 */
@Injectable()
export class ProtocolVersionService
{
  /**
   * The protocol version the client prefers, generally the most recent it
   * supports.
   */
  public preferred : number;

  /**
   * The list of protocol version numbers the client supports.
   */
  public supported : number[];

  /**
   * An array of version numbers that the server *may* support. This is a holder
   * for the negotiation process and starts equal to the supported array. Items
   * are gradually popped off and tested until a version is agreed upon.
   */
  private potential : number[];

  /**
   * The version that the handshake agreed upon, or null if there has been no
   * agreement or successful connection.
   */
  public selected : number | null = null;

  constructor (protected config : ConfigurationService)
  {
    // NOTE: We don't inject MessagingService because it actually relies on this
    // class and we don't want to create cyclic dependencies. Instead we just
    // use a callback in negotiate() to send.
    this.preferred = this.config.protocol.clientProtocolVersion;

    // Ensure the preferred version is listed LAST in the potential array
    const supported : number[]
      = Array.from(this.config.protocol.supportedProtocolVersions);
    const pos : number = supported.indexOf(this.preferred);
    if (pos < 0)
    {
      throw new Error(
        "Client preferred version not present in client's supported list.");
    }
    // supported.splice(p, 1);
    // supported.push(this.preferred);
    this.supported = supported;

    // Duplicate the supported list into a potential list for evaluation
    this.potential = Array.from(this.supported);
  }

  /**
   * Alternative way to access the currently selected version.
   */
  public get () : number | null
  {
    return this.selected;
  }

  /**
   * Unset the selected version and make all supported versions available again.
   */
  public reset () : void
  {
    this.selected = null;
    this.potential = Array.from(this.supported);
  }

  /**
   * Whether there are any versions remaining in the supported list (after
   * eliminating some number in conversation with the server).
   * @return {boolean}
   */
  private hasMorePotentials () : boolean
  {
    return (this.potential.length > 0);
  }

  /**
   * Construct a VersionRequest for the next potential version number
   */
  private next () : VersionRequest
  {
    if (this.potential.length === 0)
    {
      throw new Error("No more supported versions available!");
    }
    return new VersionRequest(this.potential.pop() as number);
  }

  /**
   * Reduce the list of potential versions to a given subset.
   */
  private suggest (versions : number []) : void
  {
    // Intersect the current array of potentials and the suggested versions
    this.potential = this.potential.filter(
      (v : number) => versions.indexOf(v) > -1);
  }

  private select (version : number) : Array<true>
  {
    this.selected = version;
    window.protocolVersion = version;
    console.log(`WS handshake succeeding, using version ${version}`);
    return [true];
  }

  /**
   * Communicate with the server using the provided send callback and establish
   * a version for the protocol, if the client and server can agree.
   */
  public negotiate (send : SendSignature, serverVersions? : number[])
    : Observable<boolean>
  {
    if (serverVersions && serverVersions.length)
    {
      this.suggest(serverVersions);
    }

    if (!this.hasMorePotentials())
    {
      // We've exhausted the list of potential versions
      return Observable.of(false);
    }

    const versionRequest : VersionRequest = this.next();
    return send(versionRequest).flatMap(
      (reply : Message) =>
        // If the reply is good, return true, else recurse & try another version
        versionRequest.verifyReply(reply)
          ? this.select(versionRequest.version)
          : this.negotiate(send, (reply as VersionResponse).supported)
      );
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/server/protocol-version.service.ts